import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TaxMastersScreen from 'components/shared/tax-master-module/TaxMastersScreen';
import type { TaxMasterBasisOption } from 'components/shared/tax-master-module/taxMaster.types';
import taxMasterService from 'api/services/taxMasterService';
import { taxMetaService } from 'api/services';
import type { TaxPackDto } from 'api/types';
import GridLoadingSkeleton from 'components/common/GridLoadingSkeleton';

/**
 * ============================================================================
 * TAX MASTERS — FBP host for the relocatable shared module (ADR-0047; TaxCore ADR-0022 §3)
 * ============================================================================
 * This file and `api/services/taxMasterService.ts` are the ONLY FBP-specific
 * pieces. `components/shared/tax-master-module/` is copied verbatim from
 * EnterpriseTemplates/react-project-template and must stay byte-identical so
 * upstream changes can be delta-synced — put app-specific behaviour HERE,
 * never in that folder.
 *
 * What differs from the template's host, and why — FBP is genuinely
 * multi-jurisdiction (Botswana VAT today, India GST next):
 *
 *   1. The jurisdiction is READ FROM THE ENTITY, not from a default pack. The
 *      template resolves `TaxModule:DefaultJurisdictionCode`; doing that here
 *      would show a Botswana entity's user a set of Indian masters they must
 *      not touch. The entity's code is then passed to GET /api/v1/tax/pack.
 *   2. Component columns and published slab bases come from THAT pack, so a
 *      BW_VAT entity gets one VAT column and no bases, while IN_GST gets
 *      CGST/SGST/IGST/CESS and the bases seed 133 publishes.
 *   3. Supply categories are chosen per jurisdiction (see supplyCategoriesFor).
 *
 * `permissionResource` is FBP's kebab screen code (`tax-masters`, seed 132):
 * the module defaults to the template's `TAX_MASTERS`, and the API's
 * TaxPermissions constants gate the same `UI.tax-masters.*` codes.
 *
 * The layout may pass `moduleIcon` (menu row icon key) and `moduleName` (tab
 * title); both are forwarded so the header follows the catalog.
 * ============================================================================
 */

/**
 * Which supply categories a tax master may carry, per jurisdiction.
 *
 * **Zero-rated is not universal.** Under GST it is a property of the SUPPLY — export or SEZ —
 * asserted on the document, where it short-circuits before any master is consulted. A zero-rated
 * master would therefore pin an item to nil wherever it is sold, which is wrong: the same item is
 * 18% domestically and zero-rated only on export. Under Botswana VAT zero-rating IS a rate family
 * (`BW_VAT_ZERO`, live for Mazars), so the option must stay available there.
 *
 * Hardcoded per jurisdiction rather than derived, because nothing publishes the valid category set
 * yet — the jurisdiction field schema is the eventual home for it (ADR-0047 Phase 2).
 */
function supplyCategoriesFor(jurisdictionCode: string): Array<{ value: string; label: string }> {
  const standard = { value: 'STANDARD', label: 'Standard' };
  const exempt = { value: 'EXEMPT', label: 'Nil / Exempt' };
  const zero = { value: 'ZERO', label: 'Zero-rated' };

  // Flat-rate VAT regimes express zero-rating as a rate family.
  if (jurisdictionCode === 'BW_VAT') return [standard, zero, exempt];

  return [standard, exempt];
}

export interface TaxMastersHostProps {
  tabId?: string;
  moduleIcon?: string;
  moduleName?: string;
}

const TaxMastersHost: React.FC<TaxMastersHostProps> = ({ tabId, moduleIcon, moduleName }) => {
  const [jurisdictionCode, setJurisdictionCode] = useState<string | null>(null);
  const [pack, setPack] = useState<TaxPackDto | null>(null);
  const [componentCodes, setComponentCodes] = useState<string[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const jurisdiction = await taxMetaService.getEntityJurisdiction();

      if (!jurisdiction?.jurisdictionCode) {
        // Not an error — an entity simply may not have a tax regime set yet. Naming the
        // screen that fixes it is more useful than an empty grid.
        setJurisdictionCode(null);
        return;
      }

      setJurisdictionCode(jurisdiction.jurisdictionCode);

      // The pack is the authority for components and bases. It fails closed when the tenant
      // schema is behind (MIGRATION_PENDING) or the tax data is invalid (TAX_PACK_INVALID):
      // both are operator actions, so the message is shown rather than an empty grid.
      try {
        const loaded = await taxMetaService.getPack(jurisdiction.jurisdictionCode);
        setPack(loaded);
        const codes = (loaded?.components ?? []).map((c) => c.code).filter(Boolean);
        setComponentCodes(codes.length > 0 ? codes : undefined);
      } catch (packErr: any) {
        setPack(null);
        setError(packErr?.response?.data?.message || packErr?.message || 'The tax pack could not be loaded for this entity.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Could not load the entity tax configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const bases = useMemo<TaxMasterBasisOption[]>(() => (pack?.bases ?? []).map((b) => ({
    value: b.basisCode,
    label: b.label,
    description: [b.unitOfMeasure, b.basisScope].filter(Boolean).join(' · ') || null,
  })), [pack]);

  if (loading) return <GridLoadingSkeleton />;

  if (error) {
    return <div style={{ padding: 24, color: 'var(--color-danger, #b42318)' }}>{error}</div>;
  }

  if (!jurisdictionCode) {
    return (
      <div style={{ padding: 24 }}>
        No tax jurisdiction is set for this entity, so there are no tax masters to maintain.
        Set one under <strong>Tax &rsaquo; Tax Settings</strong>, then return here.
      </div>
    );
  }

  return (
    <TaxMastersScreen
      tabId={tabId}
      moduleIcon={moduleIcon}
      moduleName={moduleName}
      api={taxMasterService}
      jurisdictionCode={jurisdictionCode}
      componentCodes={componentCodes ?? ['CGST', 'SGST', 'IGST', 'CESS']}
      bases={bases}
      supplyCategories={supplyCategoriesFor(jurisdictionCode)}
      permissionResource="tax-masters"
    />
  );
};

export default TaxMastersHost;
